import { z } from 'zod';
import { NOTE_COLORS, type NoteColor } from '../colors.js';
import { LIMITS } from '../limits.js';
import type { PublicUserDto } from './auth.js';

export const NOTE_TYPES = ['text', 'checklist'] as const;
export type NoteType = (typeof NOTE_TYPES)[number];

/** `all` = toutes les notes hors corbeille (archives comprises), utilisé par la recherche. */
export const NOTE_VIEWS = ['notes', 'reminders', 'archive', 'trash', 'all'] as const;
export type NoteView = (typeof NOTE_VIEWS)[number];

const uuid = z.uuid();

export const checklistItemInputSchema = z.object({
  /** Absent pour un nouvel élément : l'API génère l'identifiant. */
  id: uuid.optional(),
  text: z.string().max(LIMITS.checklistItemText),
  checked: z.boolean().default(false),
});
export type ChecklistItemInput = z.input<typeof checklistItemInputSchema>;

const reminderAt = z.iso.datetime({ offset: true }).nullable();

export const createNoteSchema = z.object({
  title: z.string().max(LIMITS.noteTitle).default(''),
  content: z.string().max(LIMITS.noteContent).default(''),
  type: z.enum(NOTE_TYPES).default('text'),
  color: z.enum(NOTE_COLORS).default('default'),
  items: z.array(checklistItemInputSchema).max(LIMITS.checklistItems).default([]),
  labelIds: z.array(uuid).max(LIMITS.labelsPerUser).default([]),
  pinned: z.boolean().default(false),
  archived: z.boolean().default(false),
  reminderAt: reminderAt.default(null),
});
export type CreateNoteInput = z.input<typeof createNoteSchema>;

export const updateNoteSchema = z
  .object({
    title: z.string().max(LIMITS.noteTitle),
    content: z.string().max(LIMITS.noteContent),
    type: z.enum(NOTE_TYPES),
    color: z.enum(NOTE_COLORS),
    /** Remplace la liste complète des éléments (ordre = ordre du tableau). */
    items: z.array(checklistItemInputSchema).max(LIMITS.checklistItems),
    labelIds: z.array(uuid).max(LIMITS.labelsPerUser),
    pinned: z.boolean(),
    archived: z.boolean(),
    reminderAt,
  })
  .partial();
export type UpdateNoteInput = z.input<typeof updateNoteSchema>;

export const listNotesQuerySchema = z.object({
  view: z.enum(NOTE_VIEWS).default('notes'),
  labelId: uuid.optional(),
  q: z.string().trim().max(LIMITS.searchQuery).optional(),
  color: z.enum(NOTE_COLORS).optional(),
  type: z.enum(NOTE_TYPES).optional(),
  hasImages: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
export type ListNotesQuery = z.input<typeof listNotesQuerySchema>;

/**
 * Déplacement d'une note dans la grille : on fournit ses voisines dans l'ordre affiché.
 * `beforeId` = note affichée juste avant (au-dessus), `afterId` = juste après.
 */
export const moveNoteSchema = z
  .object({
    beforeId: uuid.nullable().default(null),
    afterId: uuid.nullable().default(null),
  })
  .refine((v) => v.beforeId !== null || v.afterId !== null, {
    message: 'beforeId ou afterId requis',
  })
  .refine((v) => v.beforeId !== v.afterId, { message: 'beforeId et afterId doivent différer' });
export type MoveNoteInput = z.input<typeof moveNoteSchema>;

export const BULK_ACTIONS = [
  'pin',
  'unpin',
  'archive',
  'unarchive',
  'trash',
  'restore',
  'delete',
  'color',
  'addLabel',
  'removeLabel',
] as const;
export type BulkAction = (typeof BULK_ACTIONS)[number];

const ids = z.array(uuid).min(1).max(LIMITS.bulkIds);

export const bulkNotesSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.enum(['pin', 'unpin', 'archive', 'unarchive', 'trash', 'restore', 'delete']),
    ids,
  }),
  z.object({ action: z.literal('color'), ids, color: z.enum(NOTE_COLORS) }),
  z.object({ action: z.enum(['addLabel', 'removeLabel']), ids, labelId: uuid }),
]);
export type BulkNotesInput = z.infer<typeof bulkNotesSchema>;

export const addCollaboratorSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email({ message: 'Adresse e-mail invalide' })),
});
export type AddCollaboratorInput = z.infer<typeof addCollaboratorSchema>;

export interface ChecklistItemDto {
  id: string;
  text: string;
  checked: boolean;
}

export interface AttachmentDto {
  id: string;
  mimeType: string;
  size: number;
  url: string;
  createdAt: string;
}

export interface NoteDto {
  id: string;
  title: string;
  content: string;
  type: NoteType;
  color: NoteColor;
  items: ChecklistItemDto[];
  /** Propre à l'utilisateur courant (comme dans Keep, chaque collaborateur a son propre état). */
  pinned: boolean;
  archived: boolean;
  position: number;
  labelIds: string[];
  reminderAt: string | null;
  trashedAt: string | null;
  owner: PublicUserDto;
  collaborators: PublicUserDto[];
  attachments: AttachmentDto[];
  createdAt: string;
  updatedAt: string;
}

export interface BulkResultDto {
  updated: string[];
}
